<template>
  <ContentModal v-model="show" title="Volunteer Assignment Insights" size="2xl">
    <template #default>
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-success" role="status"><span class="visually-hidden">Loading...</span></div>
        <p class="mt-2 text-muted">Loading volunteer statistics...</p>
      </div>

      <div v-else-if="error" class="text-center py-4 text-danger">
        <i class="material-icons" style="font-size:48px;">error_outline</i>
        <p class="mt-2">{{ error }}</p>
      </div>

      <div v-else-if="stats">
        <!-- Summary Cards -->
        <div class="row mb-4">
          <div class="col-md-3 col-6">
            <div class="summary-stat-card"><div class="summary-stat-value">{{ stats.summary?.total_volunteers || 0 }}</div><div class="summary-stat-label">Total Volunteers</div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="summary-stat-card"><div class="summary-stat-value">{{ stats.summary?.total_assignments || 0 }}</div><div class="summary-stat-label">Total Assignments</div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="summary-stat-card"><div class="summary-stat-value">{{ stats.summary?.avg_assignments || 0 }}</div><div class="summary-stat-label">Avg per Volunteer</div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="summary-stat-card"><div class="summary-stat-value">{{ stats.summary?.overall_decline_rate || 0 }}%</div><div class="summary-stat-label">Overall Decline Rate</div></div>
          </div>
        </div>

        <!-- Charts Grid -->
        <div class="row">
          <div class="col-lg-6 mb-4" v-for="chart in chartDefs" :key="chart.key">
            <div class="chart-section">
              <h6 class="chart-title">
                <i class="material-icons me-1" :style="{ color: chart.iconColor }">{{ chart.icon }}</i>
                {{ chart.title }}
                <i class="material-icons ms-1 chart-help-icon"
                   @click.stop="openHelp = (openHelp === chart.key ? null : chart.key)"
                >help_outline</i>
              </h6>
              <div v-if="openHelp === chart.key" class="chart-help-box" v-html="chart.helpText"></div>
              <div class="chart-wrap"><canvas :id="cid(chart.key)" height="280"></canvas></div>
              <p class="chart-note">{{ chart.note }}</p>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6 mb-4">
            <div class="chart-section">
              <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <h6 class="chart-title">
                  <i class="material-icons me-1" style="color:#0f766e;">forum</i>
                  Assignment Responses by Method
                </h6>
                <span class="text-xs text-secondary">Last 60 days and next 60 days</span>
              </div>
              <div v-if="responseMethodRows.length" class="chart-wrap">
                <canvas :id="cid('response-methods')" height="280"></canvas>
              </div>
              <p v-else class="text-center text-secondary py-5 mb-0">
                No assignment responses were recorded in this period.
              </p>
              <p class="chart-note">Shows confirmed and declined assignments, grouped by response method.</p>
            </div>
          </div>

          <div class="col-lg-6 mb-4">
            <div class="chart-section">
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h6 class="chart-title mb-0">
                  <i class="material-icons me-1" style="color:#0f766e;">login</i>
                  Volunteer Logins
                </h6>
                <select v-model.number="loginDays" class="form-select form-select-sm login-range-select"
                  aria-label="Volunteer login date range">
                  <option :value="7">Last 7 days</option>
                  <option :value="30">Last 30 days</option>
                  <option :value="90">Last 90 days</option>
                </select>
              </div>
              <div v-if="loginLoading" class="text-center py-5 text-secondary">Loading login activity...</div>
              <div v-else-if="loginError" class="text-center py-5 text-danger">{{ loginError }}</div>
              <template v-else-if="loginStats">
                <div class="login-summary-grid">
                  <div class="login-summary-card">
                    <span>Unique volunteers</span>
                    <strong>{{ formatNumber(loginStats.totals?.unique_volunteers) }}</strong>
                  </div>
                  <div class="login-summary-card">
                    <span>Login events</span>
                    <strong>{{ formatNumber(loginStats.totals?.login_events) }}</strong>
                  </div>
                </div>
                <div class="chart-wrap">
                  <canvas :id="cid('login-activity')" height="280"></canvas>
                </div>
                <p class="chart-note">Daily count of unique, active volunteers with approved location access.</p>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-4 text-muted"><p>No volunteer statistics available.</p></div>
    </template>
  </ContentModal>

  <!-- Person Schedule sub-modal (z-index elevated above the insights ContentModal) -->
  <div v-if="personScheduleOpen" class="person-schedule-overlay">
    <Modal :modalHeading="personScheduleHeading" :iframeUrl="personScheduleUrl"
      size="2xl" @close="personScheduleOpen = false" />
  </div>
</template>

<script setup>
import { ref, watch, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
import ContentModal from '@/components/Modal/ContentModal.vue'
import Modal from '@/components/Modal/Modal.vue'
import Chart from 'chart.js/auto'
import api from '@/config/api'
import { baseUrl } from '@/utils/BaseUrl.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  locationId: { type: [Number, String], default: null },
})
const emit = defineEmits(['update:modelValue'])

// Local state — not Vuex — to avoid triggering Dashboard re-renders
const stats = ref(null)
const loading = ref(false)
const error = ref(null)
const loginStats = ref(null)
const loginLoading = ref(false)
const loginError = ref('')
const loginDays = ref(30)

// Person schedule sub-modal
const personScheduleOpen = ref(false)
const personScheduleHeading = ref('')
const personScheduleUrl = ref('')

// Help popover toggle (stores the chart key that's currently open, or null)
const openHelp = ref(null)

// Close help popover when clicking outside
function onDocClick() { openHelp.value = null }
onMounted(() => document.addEventListener('click', onDocClick))
onBeforeUnmount(() => document.removeEventListener('click', onDocClick))

const uid = Math.random().toString(36).slice(2, 8)
const chartInstances = []
// Map chart key → array of row data so click handler can look up person
const chartDataMap = {}
let renderTimer = null
let responseMethodChart = null
let loginChart = null
let loginRequestId = 0

const show = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

function cid(key) { return `vs-${key}-${uid}` }
const responseMethodRows = computed(() => (
  Array.isArray(stats.value?.response_methods) ? stats.value.response_methods : []
))

function formatNumber(value) {
  return Number(value || 0).toLocaleString()
}

// Colours
const CL = {
  primary: ['rgba(66,133,244,0.85)', 'rgba(66,133,244,1)'],
  danger:  ['rgba(234,67,53,0.85)',   'rgba(234,67,53,1)'],
  warning: ['rgba(251,188,4,0.85)',   'rgba(251,188,4,1)'],
  success: ['rgba(52,168,83,0.85)',   'rgba(52,168,83,1)'],
  purple:  ['rgba(124,58,237,0.85)',  'rgba(124,58,237,1)'],
  slate:   ['rgba(100,116,139,0.85)', 'rgba(100,116,139,1)'],
}

// Chart definitions — driven by data
const chartDefs = [
  {
    key: 'most', title: 'Top 10 — Most Assignments', icon: 'leaderboard', iconColor: '#4285f4', color: 'primary',
    note: 'Volunteers with the most pending or confirmed assignments (last 60 days & next 60 days).',
    helpText: '<b>Data:</b> Assignments from the last 60 days and next 60 days with a <em>pending</em> or <em>confirmed</em> status.<br><b>Shows:</b> Which volunteers have the most active assignments.<br><b>Tip:</b> Click any bar to view that volunteer\u2019s assignment details.',
  },
  {
    key: 'decline', title: 'Top 10 — Highest Decline', icon: 'trending_down', iconColor: '#ea4335', color: 'danger',
    note: 'Declined assignment count adjusted for swaps (last 60 days & next 60 days, min 3 assignments).',
    helpText: '<b>Data:</b> Declined assignments from the last 60 and next 60 days, minus any assignment-swap declines.<br><b>Shows:</b> Volunteers with the most declined assignments (bar = count, hover for %).<br><b>Tip:</b> Click any bar to view that volunteer\u2019s assignment details.',
  },
  {
    key: 'noshow', title: 'Top 10 — No-Shows', icon: 'person_off', iconColor: '#fbbc04', color: 'warning',
    note: 'Volunteers marked as not attended in the last 60 days.',
    helpText: '<b>Data:</b> Assignments from the last 60 days where attendance was marked as <em>No</em>.<br><b>Shows:</b> Volunteers with the most recorded no-shows.<br><b>Tip:</b> Click any bar to view that volunteer\u2019s assignment details.',
  },
  {
    key: 'confirm', title: 'Top 10 — Most Confirmed', icon: 'trending_up', iconColor: '#34a853', color: 'success',
    note: 'Confirmed assignment count adjusted for swaps (last 60 days & next 7 days, min 3 assignments).',
    helpText: '<b>Data:</b> Confirmed/accepted assignments from the last 60 days and next 7 days, with swap adjustments.<br><b>Shows:</b> Volunteers with the most confirmed assignments (bar = count, hover for %).<br><b>Tip:</b> Click any bar to view that volunteer\u2019s assignment details.',
  },
  {
    key: 'autodeclined', title: 'Top 10 — Most Auto-Declined Responses', icon: 'auto_delete', iconColor: '#7c3aed', color: 'purple',
    note: 'Assignments automatically declined by the system in the last 60 days.',
    helpText: '<b>Data:</b> Assignments from the last 60 days that were <em>auto-declined</em> by the system (volunteer did not respond before the deadline).<br><b>Shows:</b> Volunteers who were auto-removed the most.<br><b>Tip:</b> Click any bar to view that volunteer\u2019s assignment details.',
  },
  {
    key: 'unresponsive', title: 'Top 10 — Most Unresponsive', icon: 'hourglass_empty', iconColor: '#64748b', color: 'slate',
    note: 'Volunteers with the most pending (unanswered) assignments from the last 60 days.',
    helpText: '<b>Data:</b> Assignments from the last 60 days that still have a <em>pending</em> status.<br><b>Shows:</b> Volunteers who have not responded to the most assignments.<br><b>Tip:</b> Click any bar to view that volunteer\u2019s assignment details.',
  },
]

function openPersonSchedule(personId, name) {
  personScheduleUrl.value = `${baseUrl}/MF/cs-mf-personschedule/?personid=${personId}`
  personScheduleHeading.value = `Assignments — ${name}`
  personScheduleOpen.value = true
}

function destroyCharts() {
  if (renderTimer) { clearTimeout(renderTimer); renderTimer = null }
  while (chartInstances.length) { const c = chartInstances.pop(); if (c) c.destroy() }
  if (responseMethodChart) { responseMethodChart.destroy(); responseMethodChart = null }
  if (loginChart) { loginChart.destroy(); loginChart = null }
}

function makeChart(chartKey, labels, values, tooltipLines, color) {
  const canvasId = cid(chartKey)
  const el = document.getElementById(canvasId)
  if (!el) return
  const prev = Chart.getChart(canvasId)
  if (prev) prev.destroy()

  const [bg, border] = CL[color] || CL.primary

  const instance = new Chart(el.getContext('2d'), {
    type: 'bar',
    data: {
      labels,
      datasets: [{ data: values, backgroundColor: bg, borderColor: border, borderWidth: 1, borderRadius: 4, barThickness: 16 }],
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 400 },
      onClick(_evt, elements) {
        if (!elements.length) return
        const idx = elements[0].index
        const rows = chartDataMap[chartKey]
        if (rows && rows[idx]) {
          openPersonSchedule(rows[idx].person_id, rows[idx].name)
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label(_ctx) {
              const idx = _ctx.dataIndex
              return tooltipLines[idx] || `${_ctx.parsed.x}`
            },
          },
        },
      },
      scales: {
        x: { beginAtZero: true, grid: { display: true, color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 11 } } },
        y: {
          grid: { display: false },
          ticks: {
            font: { size: 11, weight: 'bold' },
            color: '#2563eb',
            callback(value) {
              const l = this.getLabelForValue(value)
              return l.length > 20 ? l.slice(0, 18) + '…' : l
            },
          },
        },
      },
    },
  })

  // Make y-axis labels look clickable
  instance.options.plugins.tooltip.intersect = false
  chartInstances.push(instance)
}
function makeResponseMethodChart(rows) {
  const canvasId = cid('response-methods')
  const el = document.getElementById(canvasId)
  if (!el || !rows.length) return
  const prev = Chart.getChart(canvasId)
  if (prev) prev.destroy()

  const colors = ['#0f766e', '#0284c7', '#7c3aed', '#f59e0b', '#e11d48', '#64748b', '#14b8a6']
  responseMethodChart = new Chart(el.getContext('2d'), {
    type: 'doughnut',
    data: {
      labels: rows.map(row => row.label || row.method),
      datasets: [{
        data: rows.map(row => Number(row.count || 0)),
        backgroundColor: rows.map((_row, index) => colors[index % colors.length]),
        borderColor: '#fff',
        borderWidth: 2,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label(context) {
              const row = rows[context.dataIndex]
              return `${context.label}: ${formatNumber(row.count)} (${row.percent || 0}%)`
            },
          },
        },
      },
    },
  })
}

function renderLoginChart() {
  const canvasId = cid('login-activity')
  const el = document.getElementById(canvasId)
  const rows = loginStats.value?.daily || []
  if (!el || !rows.length) return
  if (loginChart) { loginChart.destroy(); loginChart = null }
  const prev = Chart.getChart(canvasId)
  if (prev) prev.destroy()

  loginChart = new Chart(el.getContext('2d'), {
    type: 'line',
    data: {
      labels: rows.map(row => row.date.slice(5)),
      datasets: [{
        label: 'Unique volunteers',
        data: rows.map(row => Number(row.unique_volunteers || 0)),
        borderColor: '#0f766e',
        backgroundColor: 'rgba(15,118,110,0.12)',
        pointBackgroundColor: '#0f766e',
        pointRadius: rows.length > 31 ? 0 : 2,
        pointHoverRadius: 4,
        borderWidth: 2,
        fill: true,
        tension: 0.25,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label(context) {
              return `${formatNumber(context.parsed.y)} unique volunteers`
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { autoSkip: true, maxTicksLimit: 8, font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, font: { size: 11 } },
          grid: { color: 'rgba(0,0,0,0.05)' },
        },
      },
    },
  })
}

function renderCharts() {
  if (!stats.value) return
  destroyCharts()

  renderTimer = setTimeout(() => {
    const s = stats.value

    // Most Assignments
    const m = s.top_most_assignments || []
    chartDataMap.most = m
    makeChart('most', m.map(r => r.name), m.map(r => r.total),
      m.map(r => `${r.total} total (${r.confirmed} confirmed, ${r.pending} pending)`), 'primary')

    // Highest Decline (bar = count, hover = %)
    const d = s.top_decline_rate || []
    chartDataMap.decline = d
    makeChart('decline', d.map(r => r.name), d.map(r => r.declined),
      d.map(r => `${r.declined} declined / ${r.total} total (${r.decline_rate}%)`), 'danger')

    // No-Shows
    const n = s.top_no_shows || []
    chartDataMap.noshow = n
    makeChart('noshow', n.map(r => r.name), n.map(r => r.no_show),
      n.map(r => `${r.no_show} no-shows out of ${r.total} assignments`), 'warning')

    // Most Confirmed (bar = count, hover = %)
    const cr = s.top_confirm_rate || []
    chartDataMap.confirm = cr
    makeChart('confirm', cr.map(r => r.name), cr.map(r => r.confirmed),
      cr.map(r => `${r.confirmed} confirmed / ${r.total} total (${r.confirm_rate}%)`), 'success')

    // Most Auto-Declined Responses
    const ad = s.top_auto_declined || []
    chartDataMap.autodeclined = ad
    makeChart('autodeclined', ad.map(r => r.name), ad.map(r => r.auto_declined),
      ad.map(r => `${r.auto_declined} auto-declined out of ${r.total} assignments`), 'purple')

    // Most Unresponsive
    const mu = s.top_most_unresponsive || []
    chartDataMap.unresponsive = mu
    makeChart('unresponsive', mu.map(r => r.name), mu.map(r => r.pending),
      mu.map(r => `${r.pending} pending out of ${r.total} assignments`), 'slate')

    makeResponseMethodChart(s.response_methods || [])
    renderLoginChart()
  }, 120)
}

async function fetchStats() {
  if (!props.locationId) {
    stats.value = null
    error.value = 'No location selected. Re-open this report from Manage Schedules after selecting a location.'
    return
  }
  loading.value = true
  error.value = null
  try {
    const res = await api.get(`/locations/${props.locationId}/volunteer-stats`)
    stats.value = res.data?.data || res.data
  } catch (e) {
    console.error('Error fetching volunteer stats:', e)
    error.value = e?.response?.data?.message || e?.message || 'Failed to load statistics'
  } finally {
    loading.value = false
  }
}

async function fetchLoginStats() {
  if (!props.locationId) return
  const requestId = ++loginRequestId
  if (loginChart) { loginChart.destroy(); loginChart = null }
  loginStats.value = null
  loginLoading.value = true
  loginError.value = ''
  try {
    const res = await api.get(`/locations/${props.locationId}/volunteer-login-stats`, {
      params: { days: loginDays.value },
    })
    if (requestId !== loginRequestId) return
    loginStats.value = res.data?.data || null
    if (!loginStats.value) throw new Error('Login activity was not returned')
    loginLoading.value = false
    await nextTick()
    renderLoginChart()
  } catch (e) {
    if (requestId === loginRequestId) {
      loginError.value = e?.response?.data?.message || e?.message || 'Failed to load login activity'
    }
  } finally {
    if (requestId === loginRequestId) loginLoading.value = false
  }
}

watch([show, () => props.locationId], async ([v]) => {
  if (v) {
    await Promise.all([fetchStats(), fetchLoginStats()])
    renderTimer = setTimeout(() => renderCharts(), 150)
  } else {
    loginRequestId++
    destroyCharts()
  }
}, { immediate: true })

watch(loginDays, () => {
  if (show.value) fetchLoginStats()
})

onBeforeUnmount(() => destroyCharts())
</script>

<style scoped>
.summary-stat-card { background:#f8f9fa; border-radius:10px; padding:16px; text-align:center; margin-bottom:8px }
.summary-stat-value { font-size:1.5rem; font-weight:700; color:#1e293b }
.summary-stat-label { font-size:.75rem; color:#64748b; text-transform:uppercase; letter-spacing:.5px; margin-top:4px }
.chart-section { background:#fff; border:1px solid #e2e8f0; border-radius:12px; padding:16px; height:100% }
.chart-title { font-size:.85rem; font-weight:700; color:#334155; margin-bottom:12px; display:flex; align-items:center }
.chart-title .material-icons { font-size:18px }
.chart-wrap { position:relative; height:280px; cursor:pointer }
.chart-help-icon { font-size:16px; color:#94a3b8; cursor:pointer; vertical-align:middle; transition:color .15s }
.chart-help-icon:hover { color:#64748b }
.chart-help-box { background:#f0f9ff; border:1px solid #bae6fd; border-radius:8px; padding:10px 14px; margin-bottom:10px; font-size:.75rem; color:#334155; line-height:1.6 }
.chart-note { font-size:.7rem; color:#94a3b8; text-align:center; margin:8px 0 0 }
.login-range-select { width:auto; min-width:132px; }
.login-summary-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:8px; margin:14px 0; }
.login-summary-card { display:flex; flex-direction:column; gap:2px; padding:10px 12px; border:1px solid #e2e8f0; border-radius:8px; background:#f8fafc; }
.login-summary-card span { color:#64748b; font-size:.7rem; text-transform:uppercase; letter-spacing:.4px; }
.login-summary-card strong { color:#1e293b; font-size:1.25rem; }
.person-schedule-overlay { position:fixed; top:0; left:0; width:100%; height:100%; z-index:9999 }
</style>
